import {inject, Injectable} from '@angular/core';
import {CanActivate} from '@angular/router';
import {defer, Observable, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {ClientLoadingService} from '../services/auth/client-loading.service';
import {PlatformIdentityService} from '../services/auth/identity/platform-identity.service';
import {ClientStateService} from '../services/auth/client-state.service';

@Injectable({
    providedIn: 'root'
})
export class ClientLoadGuard implements CanActivate {
    private clientLoadingService = inject(ClientLoadingService);
    private identity = inject(PlatformIdentityService);
    private clientStateService = inject(ClientStateService);


    canActivate(): Observable<boolean> {
        if (this.clientStateService.getOidcCallbackDisposition() !== null) {
            return of(false);
        }

        // Angular starts guards on the same route concurrently. Restore the Auth0
        // session here too, before a child guard evaluates fresh product rights.
        const auth0 = this.identity.auth0;
        const authenticated$ = auth0
            ? defer(() => auth0.ensureSession()).pipe(map(session => Boolean(session)))
            : of(this.identity.authenticated);

        return authenticated$.pipe(
            switchMap(authenticated => {
                if (!authenticated) {
                    // Native Entra authentication remains owned by its MSAL guard.
                    return of(!auth0);
                }
                console.log('User is authenticated, ensuring clients are loaded');
                return this.clientLoadingService.ensureClientSelected().pipe(map(() => true));
            }),
            catchError(err => {
                this.clientLoadingService.markShellLoadFailure(err);
                console.error('Failed to load clients; denying protected shell navigation');
                return of(false);
            })
        );
    }
}
